import { useState, useCallback } from 'react';

interface INodeConfig {
    type: string;
    questionId?: string;
    answerCondition?: string;
    resultType?: 'not_suitable' | 'text_visit' | 'continue' | 'add_note';
    skipToQuestionId?: string;
    selectedOptions?: string[];
    customText?: string;
    triggerTiming?: 'immediate' | 'after_last_question';
    targetQuestionId?: string;
}

interface IUseNodeConfigReturn {
    nodeConfigs: Record<string, INodeConfig>;
    updateNodeConfig: (nodeId: string, config: INodeConfig) => void;
    getNodeConfig: (nodeId: string) => INodeConfig | undefined;
    validateNodeConfig: (nodeId: string) => { isValid: boolean; errors: string[] };
    clearNodeConfig: (nodeId: string) => void;
}

export const useNodeConfig = (): IUseNodeConfigReturn => {
    const [nodeConfigs, setNodeConfigs] = useState<Record<string, INodeConfig>>({});

    const updateNodeConfig = useCallback((nodeId: string, config: INodeConfig) => {
        setNodeConfigs((prev) => ({
            ...prev,
            [nodeId]: {
                ...prev[nodeId],
                ...config,
            },
        }));
    }, []);

    const getNodeConfig = useCallback((nodeId: string): INodeConfig | undefined => {
        return nodeConfigs[nodeId];
    }, [nodeConfigs]);

    const validateNodeConfig = useCallback((nodeId: string): { isValid: boolean; errors: string[] } => {
        const config = nodeConfigs[nodeId];
        if (!config) {
            return { isValid: false, errors: ['Node configuration not found'] };
        }

        const errors: string[] = [];

        // 验证基本类型
        if (!config.type) {
            errors.push('Node type is required');
        }

        // 验证问题节点
        if (config.type === 'question') {
            if (!config.questionId) {
                errors.push('Question ID is required for question nodes');
            }
        }

        // 验证结果节点
        if (config.type === 'result') {
            if (!config.resultType) {
                errors.push('Result type is required for result nodes');
            }
            
            if (config.resultType === 'continue' && !config.skipToQuestionId) {
                errors.push('Skip to question ID is required for continue result type');
            }
        }

        // 验证跳转节点
        if (config.type === 'jump') {
            if (!config.targetQuestionId) {
                errors.push('Target question ID is required for jump nodes');
            }
        }

        // 验证触发时机
        if (!config.triggerTiming) {
            errors.push('Trigger timing is required');
        }

        return {
            isValid: errors.length === 0,
            errors,
        };
    }, [nodeConfigs]);

    const clearNodeConfig = useCallback((nodeId: string) => {
        setNodeConfigs((prev) => {
            const newConfigs = { ...prev };
            delete newConfigs[nodeId];
            return newConfigs;
        });
    }, []);

    return {
        nodeConfigs,
        updateNodeConfig,
        getNodeConfig,
        validateNodeConfig,
        clearNodeConfig,
    };
};
